import { Link, useParams } from 'react-router';
import { comparableUnitPrice, formatPeso, type PriceStats } from '@tahi/core';
import { Money, ScreenHeader } from '../../../ui';
import { LineChart } from '../../../ui/charts';
import { shortDay } from '../../spending/format';
import { copy, PRICES_BACK } from '../copy';
import { lineUnitPrice, quantityText, unitPriceText } from '../format';
import { usePriceStats } from '../hooks';
import { CARD, FOCUS, LABEL, SCREEN } from '../styles';
import { changeText } from './PricesScreen';

/** A value in the stats' basis: per unit ('₱59/kg') or price paid ('₱295'). */
function basisText(s: PriceStats, value: number): string {
  return s.basis === 'unit' && s.unit ? unitPriceText(value, s.unit) : formatPeso(value);
}

/**
 * Price history for one item (spec §6): last, lowest and average price, change since last time,
 * price per unit so pack sizes compare, a small chart, and every price point.
 */
export function ItemPriceScreen() {
  const { key = '' } = useParams();
  const stats = usePriceStats();
  const s = stats?.get(decodeURIComponent(key));

  if (!stats || !s) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.priceHistory} back={PRICES_BACK} />
        <p className="text-muted">{stats ? copy.priceNotFound : copy.loading}</p>
      </main>
    );
  }
  const change = changeText(s);
  const value = (p: PriceStats['points'][number]) =>
    s.basis === 'unit' ? Math.round(comparableUnitPrice(p)) : p.priceMinor;

  return (
    <main className={SCREEN}>
      <div className="flex flex-col gap-1">
        <ScreenHeader title={s.last.name} back={PRICES_BACK} />
        <p className={LABEL}>{copy.priceHistory}</p>
      </div>

      <section aria-label={copy.priceHistory} className={`${CARD} flex flex-col gap-3`}>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <dt className={LABEL}>{copy.last}</dt>
            <dd
              className="font-mono text-[26px] leading-tight tabular-nums text-fg"
              data-testid="price-last"
            >
              {formatPeso(s.last.priceMinor)}
            </dd>
            <dd className="text-xs text-muted">
              {[
                quantityText(s.last.quantity, s.last.unit),
                lineUnitPrice(s.last.priceMinor, s.last.quantity, s.last.unit),
                s.last.store,
                shortDay(s.last.date),
              ]
                .filter(Boolean)
                .join(' · ')}
            </dd>
          </div>
          <div>
            <dt className={LABEL}>{copy.change}</dt>
            <dd className="text-fg">{change ?? copy.firstPrice}</dd>
          </div>
          <div>
            <dt className={LABEL}>{copy.lowest}</dt>
            <dd className="font-mono tabular-nums text-fg">{basisText(s, s.lowestValue)}</dd>
            {s.lowest.store ? <dd className="text-xs text-muted">{s.lowest.store}</dd> : null}
          </div>
          <div>
            <dt className={LABEL}>{copy.average}</dt>
            <dd className="font-mono tabular-nums text-fg">{basisText(s, s.averageValue)}</dd>
          </div>
        </dl>
        <p className="text-xs text-muted">
          {s.basis === 'unit' && s.unit ? copy.basisUnit(s.unit) : copy.basisPaid}
        </p>
        {s.points.length > 1 ? (
          <LineChart
            ariaLabel={copy.chartLabel(s.last.name)}
            points={s.points.map((p) => ({
              key: p.tripItemId,
              label: shortDay(p.date),
              value: value(p),
            }))}
            formatValue={(v) => basisText(s, Math.round(v))}
          />
        ) : null}
      </section>

      <ol
        aria-label={copy.timesBought(s.points.length)}
        className={`${CARD} flex flex-col divide-y divide-divider py-1`}
      >
        {[...s.points].reverse().map((p) => {
          const unit = lineUnitPrice(p.priceMinor, p.quantity, p.unit);
          return (
            <li key={p.tripItemId}>
              <Link
                to={`/m/shopping/trips/${p.tripId}`}
                className={`flex min-h-12 items-center justify-between gap-3 rounded-xl py-2 ${FOCUS}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-fg">
                    {shortDay(p.date, p.date.slice(0, 4) !== s.last.date.slice(0, 4))}
                    {p.store ? ` · ${p.store}` : ''}
                  </span>
                  {quantityText(p.quantity, p.unit) || unit ? (
                    <span className="block truncate text-xs text-muted">
                      {[quantityText(p.quantity, p.unit), unit].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </span>
                <Money
                  minor={p.priceMinor}
                  cents="auto"
                  animate={false}
                  className="shrink-0 text-fg"
                />
              </Link>
            </li>
          );
        })}
      </ol>
    </main>
  );
}
